import React, { useState, useCallback } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation } from '../hooks/useApiCall';
import { enrollInCourse } from '../services/courseService';
import { getErrorMessage } from '../utils/errorHandler';

const EnrollmentModal = ({ show, onHide, course }) => {
  const [successMessage, setSuccessMessage] = useState('');

  const enrollFn = useCallback(
    ({ courseId, formData }) => enrollInCourse(courseId, formData),
    []
  );

  const { mutate, loading, error } = useMutation(enrollFn);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const handleClose = () => {
    reset();
    setSuccessMessage('');
    onHide();
  };

  const onSubmit = async (formData) => {
    if (!course) return;
    try {
      await mutate({ courseId: course.id, formData });
      const isFree = Number(course.price) === 0;
      setSuccessMessage(
        isFree
          ? '¡Inscripcion confirmada! Revisa tu email para mas detalles.'
          : '¡Registro exitoso! Te contactaremos para coordinar el pago.'
      );
      reset();
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch {
      // error already set by useMutation
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Inscribirme al Curso</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {course && (
          <p className="text-muted mb-3">
            <strong>{course.title}</strong>
          </p>
        )}

        {successMessage && (
          <Alert variant="success" className="mb-3">
            {successMessage}
          </Alert>
        )}

        {error && !successMessage && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {!successMessage && (
          <Form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Form.Group className="mb-3" controlId="enroll-name">
              <Form.Label>Nombre completo <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Tu nombre completo"
                isInvalid={!!errors.name}
                {...register('name', { required: 'El nombre es obligatorio.' })}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="enroll-email">
              <Form.Label>Correo electronico <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                placeholder="tu@correo.com"
                isInvalid={!!errors.email}
                {...register('email', {
                  required: 'El correo electronico es obligatorio.',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Ingresa un correo electronico valido.',
                  },
                })}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="enroll-phone">
              <Form.Label>Telefono <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control
                type="tel"
                placeholder="+1 (809) 000-0000"
                {...register('phone')}
              />
            </Form.Group>

            <Form.Group className="mb-4" controlId="enroll-notes">
              <Form.Label>Notas adicionales <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="¿Tienes alguna pregunta o comentario?"
                {...register('notes')}
              />
            </Form.Group>

            <div className="d-grid">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Enviando...
                  </>
                ) : (
                  'Confirmar inscripcion'
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EnrollmentModal;
